import React, { useMemo } from "react";
import "./Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const Terms = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients worldwide",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Terms of use</p>
          <h1>Terms &amp; Conditions</h1>
          <p>
            These Terms &amp; Conditions (“Terms”) set out the basic rules for
            using the {brand.name} website and working with our team. By
            visiting our site, submitting an inquiry, or engaging our services,
            you acknowledge that you’ve read and agree to these Terms.
          </p>
          <p className="pp-meta">
            <span>Provider: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
        </header>

        {/* Content */}
        <div className="privacy-policy-content">
          <section>
            <h2>1. About these Terms</h2>
            <p>
              These Terms apply to your use of our website, contact channels,
              and any services we provide directly to you. They exist alongside
              any separate proposal, statement of work, or service agreement
              that we may sign with you. If there is a conflict, the signed
              agreement will usually take priority for that project.
            </p>
          </section>

          <section>
            <h2>2. Using our website &amp; content</h2>
            <p>
              You agree to use our website in a lawful manner and not to
              interfere with its security, availability, or other visitors’
              experience. All content on this site—including text, visuals,
              layouts, and branding—is owned or licensed by {brand.name} and is
              provided for information and inspiration only. You may not copy,
              resell, or misrepresent our content as your own.
            </p>
          </section>

          <section>
            <h2>3. Proposals, scope &amp; changes</h2>
            <p>
              Any services we deliver (for example, strategy, design,
              development, or marketing) are defined in writing—usually via a
              proposal, email confirmation, or service agreement. That document
              sets out scope, timelines, responsibilities, and fees.
            </p>
            <p>
              If you request changes or additions beyond the agreed scope, we
              may provide a revised estimate or treat the extra work as a
              separate phase or mini-project.
            </p>
          </section>

          <section>
            <h2>4. Client responsibilities</h2>
            <p>To keep projects moving smoothly, you agree to:</p>
            <ul>
              <li>
                Provide timely access to any platforms, assets, or stakeholders
                we reasonably need.
              </li>
              <li>
                Supply content, approvals, and feedback within agreed response
                times.
              </li>
              <li>
                Ensure all information you share with us is accurate and does
                not infringe the rights of others.
              </li>
            </ul>
            <p>
              Delays in content, approvals, or payments may impact launch dates
              and timelines and may require schedule or budget adjustments.
            </p>
          </section>

          <section>
            <h2>5. Fees, invoicing &amp; refunds</h2>
            <p>
              Fees, payment schedules, and currency will be outlined in your
              proposal or agreement. Unless otherwise stated:
            </p>
            <ul>
              <li>Invoices are due within the payment term shown on the invoice.</li>
              <li>
                Work may be paused if payments are significantly overdue, after
                reasonable notice.
              </li>
              <li>
                Upfront deposits and fees for work already completed are
                generally non-refundable.
              </li>
            </ul>
            <p>
              Third-party costs (such as ad spend, hosting, or software
              subscriptions) are usually billed separately or paid directly by
              you to the provider.
            </p>
          </section>

          <section>
            <h2>6. Intellectual property</h2>
            <p>
              Unless we agree otherwise in writing, {brand.name} retains
              ownership of our pre-existing tools, frameworks, and know-how used
              during a project.
            </p>
            <p>
              Once all agreed fees have been paid in full, you receive the
              rights to use the final deliverables described in your agreement
              (for example, website designs, copy, or code) for your own
              business purposes. You may not resell our work as a template,
              white-label solution, or product unless explicitly permitted.
            </p>
          </section>

          <section>
            <h2>7. Third-party tools, platforms &amp; access</h2>
            <p>
              Many projects involve third-party services such as hosting
              providers, analytics tools, CRM platforms, or advertising
              networks. These services are governed by their own terms and
              privacy policies, which you agree to review and accept where
              required.
            </p>
            <p>
              We cannot control the uptime, pricing, or policy changes of these
              third-party providers and are not responsible for issues caused by
              them, though we will try to help you troubleshoot where
              reasonable.
            </p>
          </section>

          <section>
            <h2>8. Confidentiality</h2>
            <p>
              Both you and {brand.name} agree to keep each other’s non-public
              information confidential. This includes, for example, business
              plans, internal documents, account access, and customer data.
            </p>
            <p>
              We may share information with trusted team members or
              subcontractors only as needed to deliver your project, and we
              expect them to follow similar confidentiality obligations.
              Confidentiality continues even after a project ends.
            </p>
          </section>

          <section>
            <h2>9. Disclaimers</h2>
            <p>
              We aim to provide thoughtful, professional work. However, results
              in areas like search rankings, ad performance, conversions, or
              revenue depend on many factors beyond our control (such as market
              conditions, competition, budget, and internal processes).
            </p>
            <p>
              For that reason, {brand.name} does not guarantee specific business
              outcomes and the content on this site should not be taken as legal,
              financial, or tax advice.
            </p>
          </section>

          <section>
            <h2>10. Limitation of liability</h2>
            <p>
              To the extent permitted by law, {brand.name} and its team shall
              not be liable for any indirect, incidental, special, or
              consequential damages arising from the use of our website or
              services.
            </p>
            <p>
              Our total aggregate liability for any claim related to our
              services is limited to the fees you paid to us for the specific
              project or service in the three (3) months immediately preceding
              the event giving rise to the claim.
            </p>
          </section>

          <section>
            <h2>11. Term &amp; termination</h2>
            <p>
              The duration of our engagement will be defined in your proposal or
              agreement. Either party may normally end the engagement by giving
              written notice, subject to any notice period specified in the
              agreement.
            </p>
            <p>
              You remain responsible for paying for work completed (and
              committed third-party costs) up to the termination date.
            </p>
          </section>

          <section>
            <h2>12. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time to reflect changes in
              our services, business, or applicable law. When we make material
              changes, we will adjust the “Last updated” date at the top of this
              page. Your continued use of our website or services after changes
              are published constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>13. Governing law &amp; disputes</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which{" "}
              {brand.name} is established, unless another jurisdiction is
              specified in a signed agreement. Any disputes will be subject to
              the exclusive jurisdiction of the courts in that location.
            </p>
          </section>

          <section>
            <h2>14. Relationship with our Privacy Policy</h2>
            <p>
              For information about how we collect, use, and protect personal
              data, please also review our{" "}
              <a href="/privacy-policy">Privacy Policy</a>. Both documents work
              together to explain how we operate and how your information is
              handled.
            </p>
          </section>

          <section>
            <h2>15. Contact</h2>
            <p>
              If you have questions about these Terms, would like to clarify how
              they apply to your project, or wish to raise a concern, you can
              reach us at:
            </p>
            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {brand.email}
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                  {brand.phone}
                </a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
};

export default Terms;
